/* eslint-disable no-useless-catch */
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  deleteUser
} from 'firebase/auth'
import { doc, setDoc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore'
import {
  getDownloadURL,
  ref,
  uploadBytes,
  deleteObject
} from 'firebase/storage'
import { AUTH, DB, STORAGE } from '../../configs/firebase'
import { formatDateForDB } from '../../utils/date'

export default {
  namespaced: true,
  state: {
    authUser: null,
    user: {
      id: null,
      name: null,
      email: null,
      image: null,
      filename: null,
      role: null,
      provider: null
    }
  },
  getters: {
    AUTH_USER: (state) => state.authUser,
    USER: (state) => state.user
  },
  mutations: {
    SET_AUTH_USER(state, payload) {
      state.authUser = payload
    },
    SET_USER(state, payload) {
      state.user = payload
    },
    RESET_USER(state) {
      state.user = {
        name: null,
        email: null,
        image: null,
        filename: null,
        role: null,
        provider: null
      }
    },
    UPDATE_NAME(state, payload) {
      state.user.name = payload
    },
    SET_USER_IMAGE(state, payload) {
      state.user.image = payload
    },
    REMOVE_USER_IMAGE(state) {
      state.user.image = null
    }
  },
  actions: {
    async REGISTER({ commit }, { name, email, password }) {
      try {
        const user = await createUserWithEmailAndPassword(AUTH, email, password)
        const newUserDoc = doc(DB, 'users', user.user.uid)
        await setDoc(newUserDoc, {
          name,
          email,
          role: 'user'
        })

        commit('SET_AUTH_USER', user)
        commit('SET_USER', {
          id: user.user.id,
          name,
          email,
          role: 'user'
        })
        return user
      } catch (err) {
        throw err
      }
    },
    async LOGIN(_, { email, password }) {
      try {
        await signInWithEmailAndPassword(AUTH, email, password)
        return true
      } catch (err) {
        throw err
      }
    },
    async LOGIN_THROUGH_GOOGLE({ commit }) {
      const google = new GoogleAuthProvider()
      const result = await signInWithPopup(AUTH, google)

      if (!result) {
        return false
      }

      const { displayName, email, photoURL, uid } = result.user

      const userRef = await getDoc(doc(DB, 'users', uid))
      if (userRef.exists()) {
        return false
      }

      const newUser = {
        name: displayName,
        email,
        image: photoURL,
        filename: null,
        createdAt: formatDateForDB(new Date()),
        provider: 'Google',
        role: 'user'
      }

      const newUserDoc = doc(DB, 'users', uid)
      await setDoc(newUserDoc, newUser)

      commit('SET_AUTH_USER', result.user)
      commit('SET_USER', newUser)
      return result
    },
    async LOGIN_THROUGH_FACEBOOK({ commit }) {
      const facebook = new FacebookAuthProvider()
      const result = await signInWithPopup(AUTH, facebook)

      const { displayName, email, photoURL, uid } = result.user

      const userRef = await getDoc(doc(DB, 'users', uid))
      if (userRef.exists()) {
        return false
      }

      const newUser = {
        name: displayName,
        email,
        image: photoURL,
        filename: null,
        createdAt: formatDateForDB(new Date()),
        provider: 'Google',
        role: 'user'
      }

      const newUserDoc = doc(DB, 'users', uid)
      await setDoc(newUserDoc, newUser)

      commit('SET_AUTH_USER', result.user)
      commit('SET_USER', newUser)
      return result
    },
    async LOGOUT({ commit }) {
      await signOut(AUTH)
      commit('SET_AUTH_USER', null)
      commit('RESET_USER', null)
      window.localStorage.clear()
      return true
    },
    async LOAD_LOGGED_IN_USER({ commit }, id) {
      const docRef = doc(DB, 'users', id)
      const userRef = await getDoc(docRef)

      if (userRef.exists()) {
        const user = userRef.data()
        user.id = userRef.id
        commit('SET_USER', user)
        return user
      }
    },
    async GET(_, id) {
      const docRef = doc(DB, 'users', id)
      const userRef = await getDoc(docRef)

      if (!userRef.exists()) {
        return null
      }

      const user = userRef.data()
      user.id = userRef.id
      return user
    },
    async EDIT_NAME({ commit, state }, newName) {
      try {
        const docRef = doc(DB, 'users', state.user.id)
        await updateDoc(docRef, { name: newName })
        commit('UPDATE_NAME', newName)
        return true
      } catch (err) {
        console.log('err while editing name: ', err)
        throw err
      }
    },
    async UPDATE_DISPLAY_PHOTO({ commit, state }, { file }) {
      try {
        const fileType = file.type.split('/')[1]
        const photoRef = ref(STORAGE, `users/${state.user.id}.${fileType}`)
        await uploadBytes(photoRef, file, { contentType: `image/${fileType}` })
        const downloadURL = await getDownloadURL(photoRef)
        const docRef = doc(DB, 'users', state.user.id)
        await updateDoc(docRef, {
          image: downloadURL,
          filename: `${state.user.id}.${fileType}`
        })

        commit('SET_USER_IMAGE', downloadURL)
        return true
      } catch (err) {
        console.log('err while updating display picture: ', err)
        throw err
      }
    },
    async REMOVE_DISPLAY_PHOTO({ commit, state }) {
      try {
        if (state.user.image && state.user.filename) {
          const photoRef = ref(STORAGE, state.user.image)
          await deleteObject(photoRef)
        }

        const docRef = doc(DB, 'users', state.user.id)
        await updateDoc(docRef, { image: null, filename: null })
        commit('REMOVE_USER_IMAGE')
        return false
      } catch (err) {
        console.log('err while deleting display picture: ', err)
        throw err
      }
    },
    async DELETE_ACCOUNT({ commit, state, dispatch }) {
      try {
        const currentUser = AUTH.currentUser
        await deleteUser(currentUser)
        await dispatch('LOGOUT')
      } catch (err) {
        if (err && err.code && err.message) {
          if (
            err.code === 'auth/requires-recent-login' &&
            state.user.provider.toLowerCase() === 'google'
          ) {
            await dispatch('LOGIN_THROUGH_GOOGLE')
            await dispatch('DELETE_ACCOUNT')
          } else if (
            err.code === 'auth/requires-recent-login' &&
            state.user.provider.toLowerCase() === 'facebook'
          ) {
            await dispatch('LOGIN_THROUGH_FACEBOOK')
            await dispatch('DELETE_ACCOUNT')
          }
        }
      }

      try {
        const docRef = doc(DB, 'users', state.user.id)
        await deleteDoc(docRef)

        if (state.user.image && state.user.filename) {
          const photoRef = ref(STORAGE, state.user.image)
          await deleteObject(photoRef)
        }

        commit('RESET_USER')
        return true
      } catch (err) {
        console.log('err while deleting user: ', err)
        return false
      }
    }
  }
}
